import type { JSX } from 'react';
import { currentMonth, formatEur, sumEuros } from '@/lib/format';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ChargeResource } from '@majuscule/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ellipsis, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useToast } from './toast';
import { internalFetch, internalSend } from '../lib/api';
import { Callout } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardFoot, CardHead } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Field, Fields2, InputGroup } from '@/components/ui/field';
import { MonthInput } from '@/components/ui/date-input';
import { Input } from '@/components/ui/input';
import { formatMonthLabel } from '@/lib/date-input';
import { Row4, Stack } from '@/components/ui/layout-grid';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { NativeSelect } from '@/components/ui/native-select';
import { StatCard } from '@/components/ui/stat-card';
import {
  Cell2,
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const EURO_RE = /^\d+(\.\d{1,6})?$/;

const chargeFormSchema = z.object({
  label: z.string().min(1, 'Libellé obligatoire').max(200),
  frequency: z.enum(['monthly', 'annual']),
  billed: z.string().regex(EURO_RE, 'Montant en euros'),
  cost: z.string().regex(EURO_RE, 'Montant en euros'),
  startMonth: z.string().regex(MONTH_RE, 'Mois de début requis'),
  endMonth: z.string().optional(),
});
type FormValues = z.infer<typeof chargeFormSchema>;

const FREQUENCY_LABELS: Record<string, string> = {
  monthly: 'Mensuel',
  annual: 'Annuel (lissé)',
};

const COLUMN_COUNT = 7;

export function ClientCharges({ clientId }: { clientId: string }): JSX.Element {
  const qc = useQueryClient();
  const { notify } = useToast();
  const base = `/internal/clients/${clientId}/charges`;

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ChargeResource | null>(null);
  const [deleting, setDeleting] = useState<ChargeResource | null>(null);

  const charges = useQuery({
    queryKey: ['charges', clientId],
    queryFn: () => internalFetch<{ charges: ChargeResource[] }>(base),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(chargeFormSchema),
    defaultValues: {
      label: '',
      frequency: 'monthly',
      billed: '',
      cost: '0',
      startMonth: currentMonth(),
      endMonth: '',
    },
  });

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['charges', clientId] });
    void qc.invalidateQueries({ queryKey: ['client-billing', clientId] });
  };

  const toPayload = (values: FormValues): Record<string, unknown> => ({
    label: values.label,
    frequency: values.frequency,
    billed: values.billed,
    cost: values.cost === '' ? '0' : values.cost,
    startMonth: values.startMonth,
    endMonth:
      values.endMonth !== undefined && MONTH_RE.test(values.endMonth) ? values.endMonth : null,
  });

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      editing === null
        ? internalSend<ChargeResource>('POST', base, toPayload(values))
        : internalSend<ChargeResource>('PATCH', `${base}/${editing.id}`, toPayload(values)),
    onSuccess: () => {
      closeModal();
      refresh();
      notify(editing === null ? 'Charge ajoutée' : 'Charge mise à jour');
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => internalSend('DELETE', `${base}/${id}`),
    onSuccess: () => {
      setDeleting(null);
      refresh();
      notify('Charge supprimée');
    },
  });

  const openCreate = (): void => {
    setEditing(null);
    form.reset({
      label: '',
      frequency: 'monthly',
      billed: '',
      cost: '0',
      startMonth: currentMonth(),
      endMonth: '',
    });
    setOpen(true);
  };

  const openEdit = (charge: ChargeResource): void => {
    setEditing(charge);
    form.reset({
      label: charge.label,
      frequency: charge.frequency,
      billed: charge.billed,
      cost: charge.cost,
      startMonth: charge.startMonth,
      endMonth: charge.endMonth ?? '',
    });
    setOpen(true);
  };

  const closeModal = (): void => {
    setOpen(false);
    setEditing(null);
    save.reset();
    form.reset();
  };

  const rows = charges.data?.charges ?? [];
  const active = rows.filter((c) => c.active);
  const sum = (pick: (c: ChargeResource) => string): string => sumEuros(active.map(pick));
  const totalBilled = sum((c) => c.monthlyBilled);
  const totalCost = sum((c) => c.monthlyCost);
  const totalMargin = sum((c) => c.monthlyMargin);
  const errors = form.formState.errors;

  return (
    <Stack>
      <Row4 aria-label="Charges récurrentes du mois">
        <StatCard label="Facturé / mois" value={formatEur(totalBilled)} foot="charges actives" />
        <StatCard label="Coût réel / mois" value={formatEur(totalCost)} foot="interne" />
        <StatCard label="Marge / mois" value={formatEur(totalMargin)} foot="facturé − coût" />
      </Row4>

      <Card>
        <CardHead
          title="Abonnements et frais"
          description="Lignes récurrentes ajoutées au prélèvement mensuel, en plus de l'usage (IA, SMS)."
          className="pb-4"
          actions={
            <Button type="button" size="sm" onClick={openCreate}>
              <Plus aria-hidden="true" />
              Ajouter une charge
            </Button>
          }
        />
        <Table className="min-w-[720px]">
          <TableHeader>
            <TableRow>
              <TableHead kind="name">Libellé</TableHead>
              <TableHead>Fréquence</TableHead>
              <TableHead kind="num">Facturé / mois</TableHead>
              <TableHead kind="num">Coût réel / mois</TableHead>
              <TableHead kind="num">Marge / mois</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead kind="act">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((charge) => (
              <TableRow key={charge.id}>
                <TableCell kind="name">
                  <Cell2
                    primary={charge.label}
                    secondary={`${formatEur(charge.billed)} ${charge.frequency === 'annual' ? '/ an' : '/ mois'}${
                      charge.endMonth !== null
                        ? ` · jusqu'à ${formatMonthLabel(charge.endMonth)}`
                        : ''
                    }`}
                  />
                </TableCell>
                <TableCell>{FREQUENCY_LABELS[charge.frequency] ?? charge.frequency}</TableCell>
                <TableCell kind="num">
                  {Number(charge.billed) === 0 ? (
                    <Cell2 primary={formatEur(charge.monthlyBilled)} secondary="non facturée" />
                  ) : (
                    formatEur(charge.monthlyBilled)
                  )}
                </TableCell>
                <TableCell kind="num">{formatEur(charge.monthlyCost)}</TableCell>
                <TableCell kind="num" className="font-medium">
                  {formatEur(charge.monthlyMargin)}
                </TableCell>
                <TableCell>
                  <Badge variant={charge.active ? 'success' : 'neutral'}>
                    {charge.active ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell kind="act">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Actions pour ${charge.label}`}
                      >
                        <Ellipsis aria-hidden="true" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onSelect={() => {
                          openEdit(charge);
                        }}
                      >
                        <Pencil aria-hidden="true" />
                        Modifier
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        variant="destructive"
                        onSelect={() => {
                          setDeleting(charge);
                        }}
                      >
                        <Trash2 aria-hidden="true" />
                        Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {charges.isLoading ? (
              <TableEmptyRow colSpan={COLUMN_COUNT}>Chargement…</TableEmptyRow>
            ) : null}
            {rows.length === 0 && !charges.isLoading ? (
              <TableEmptyRow colSpan={COLUMN_COUNT}>
                Aucune charge. Ajoutez un abonnement ou des frais récurrents.
              </TableEmptyRow>
            ) : null}
          </TableBody>
        </Table>
        <CardFoot
          start={`${active.length} charge${active.length > 1 ? 's' : ''} active${active.length > 1 ? 's' : ''} sur ${rows.length}`}
          end="Annuel : montant lissé sur 12 mois"
        />
      </Card>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            closeModal();
          }
        }}
      >
        <DialogContent>
          <DialogHead
            title={editing === null ? 'Nouvelle charge' : 'Modifier la charge'}
            description="Montant facturé au client et coût réel (interne) pour la marge. À 0 €, la charge compte dans la marge sans apparaître sur la facture."
          />
          <form
            id="charge-form"
            onSubmit={(event) => {
              void form.handleSubmit((values) => {
                save.mutate(values);
              })(event);
            }}
            className="contents"
          >
            <DialogBody>
              <Field label="Libellé" required error={errors.label?.message}>
                <Input placeholder="Abonnement Support" {...form.register('label')} />
              </Field>
              <Field label="Fréquence" required error={errors.frequency?.message}>
                <NativeSelect {...form.register('frequency')}>
                  <option value="monthly">Mensuel</option>
                  <option value="annual">Annuel (lissé sur 12 mois)</option>
                </NativeSelect>
              </Field>
              <Fields2>
                <Field label="Facturé au client" required error={errors.billed?.message}>
                  <InputGroup suffix="€">
                    <Input
                      numeric
                      inputMode="decimal"
                      placeholder="20"
                      {...form.register('billed')}
                    />
                  </InputGroup>
                </Field>
                <Field label="Coût réel" error={errors.cost?.message}>
                  <InputGroup suffix="€">
                    <Input numeric inputMode="decimal" placeholder="0" {...form.register('cost')} />
                  </InputGroup>
                </Field>
              </Fields2>
              <Fields2>
                <Field label="Mois de début" required error={errors.startMonth?.message}>
                  <Controller
                    control={form.control}
                    name="startMonth"
                    render={({ field }) => (
                      <MonthInput
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                      />
                    )}
                  />
                </Field>
                <Field label="Mois de fin" hint="Optionnel." error={errors.endMonth?.message}>
                  <Controller
                    control={form.control}
                    name="endMonth"
                    render={({ field }) => (
                      <MonthInput
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                      />
                    )}
                  />
                </Field>
              </Fields2>
              {save.isError ? (
                <Callout variant="destructive">Échec de l&apos;enregistrement.</Callout>
              ) : null}
            </DialogBody>
          </form>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeModal}>
              Annuler
            </Button>
            <Button type="submit" form="charge-form" size="lg" disabled={save.isPending}>
              {editing === null ? 'Ajouter' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => {
          setDeleting(null);
        }}
        onConfirm={() => {
          if (deleting !== null) {
            remove.mutate(deleting.id);
          }
        }}
        title={deleting !== null ? `Supprimer « ${deleting.label} » ?` : 'Supprimer ?'}
        subtitle="Action irréversible."
        confirmLabel="Supprimer"
        busy={remove.isPending}
        variant="destructive"
        note="Les factures déjà émises conservent leur montant. Pour arrêter une charge en gardant l'historique, renseignez plutôt un mois de fin."
      />
    </Stack>
  );
}
